import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';

import type {
  ApplePayIOSConfig,
  ApplePayWebConfig,
} from '../../../components/payments/till/PaymentMethodSelector/types';
import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import type {CartStore} from '../../../stores/cartStore';
import {logger, serializeError} from '../../../utils/logger';

import {createApplePayPaymentHandler} from './useApplePayPaymentHandler';

export interface CreateApplePayCheckoutConfigsParams {
  cartStore: CartStore;
  /** Apple Pay service ID from usePaymentServices */
  applePayServiceId: string | null;
  /** Log tag for identifying source screen */
  logTag?: string;
}

export interface ApplePayCheckoutConfigs {
  /** Web Apple Pay config (undefined on non-web or if no service ID) */
  applePayConfig: ApplePayWebConfig | undefined;
  /** iOS Apple Pay config (undefined on non-iOS) */
  applePayIOSConfig: ApplePayIOSConfig | undefined;
}

/**
 * Creates Apple Pay checkout configurations for both web and iOS.
 * Consolidates Apple Pay config logic from UnifiedCheckoutScreen and CheckoutPaymentScreen.
 *
 * Payment request is fetched from backend when user clicks the Apple Pay button.
 * This function only sets up the handlers and passes the service ID.
 *
 * For gift card split payments, passes remainingBalanceCents so Apple Pay shows
 * the correct remaining amount instead of the full cart total.
 */
export function createApplePayCheckoutConfigs({
  cartStore,
  applePayServiceId,
  logTag = 'ApplePayCheckout',
}: CreateApplePayCheckoutConfigsParams): ApplePayCheckoutConfigs {
  // Shared handler params
  const handlerParams = {
    cartStore,
    logTag,
  };

  // Shared error handler for cancel/error callbacks
  const handleCancel = () => {
    logger.info(logTag, 'Apple Pay payment cancelled');
  };

  const handleError = (error: Error) => {
    logger.error(logTag, 'Apple Pay error', serializeError(error));
    Toast.show({
      type: ERROR_MESSAGE,
      text1: error.message || 'Apple Pay error occurred',
    });
  };

  // Calculate remaining balance for gift card split payments
  const remainingBalanceCents = cartStore.isGiftCardsBeingUsed
    ? cartStore.remainingBalanceAfterGiftCardsCents
    : undefined;

  // Web config - serviceId-based (backend provides payment request)
  const applePayConfig: ApplePayWebConfig | undefined =
    Platform.OS === 'web' && applePayServiceId
      ? {
          serviceId: applePayServiceId,
          remainingBalanceCents,
          onPaymentAuthorized: createApplePayPaymentHandler({...handlerParams, source: 'web'}),
          onCancel: handleCancel,
          onError: handleError,
        }
      : undefined;

  // iOS config - serviceId-based (backend provides payment request)
  const applePayIOSConfig: ApplePayIOSConfig | undefined =
    Platform.OS === 'ios' && applePayServiceId
      ? {
          serviceId: applePayServiceId,
          remainingBalanceCents,
          totalAmount: cartStore.totalAmount,
          currentOrder: cartStore.currentOrder ?? {order_lines: [], adjustments: []},
          isDelivery: cartStore.isDelivery,
          onPaymentAuthorized: createApplePayPaymentHandler({...handlerParams, source: 'ios'}),
          onCancel: handleCancel,
          onError: handleError,
        }
      : undefined;

  return {applePayConfig, applePayIOSConfig};
}
